import { useEffect, useState, useCallback, useRef } from "react";
import { api, EGG_ORDER, DEFECT_ORDER, MULTIPLIERS, BREED_LABELS, calculateFlockAge, fmt, fmtDec, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { offlineCarryover } from "@/lib/offline";
import { PageHeader, EmptyRow } from "@/components/shared";
import { toast } from "sonner";
import { Save, Trash2, Calculator } from "lucide-react";

const emptyForm = (coopId) => ({
  coop_id: coopId || "",
  date: new Date().toISOString().slice(0, 10),
  flock_age_weeks: "",
  deaths: "",
  sizes: {},
  breedSizes: {},
  defects: {},
});

export default function Production() {
  const { coops, myCoops, meta, selectedCoop } = useApp();
  const subCoopsAll = myCoops.filter((c) => c.type === "coop");
  const locations = coops.filter((c) => c.type === "location" && subCoopsAll.some((s) => s.parent_id === c.id));
  const [form, setForm] = useState(emptyForm(""));
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [locId, setLocId] = useState("");
  const [carryover, setCarryover] = useState(0);
  const [carryoverKnown, setCarryoverKnown] = useState(true);
  const firstInput = useRef(null);
  const subCoops = subCoopsAll.filter((c) => c.parent_id === locId);
  const selectedSub = subCoopsAll.find((c) => c.id === form.coop_id);
  const age = calculateFlockAge(selectedSub?.flock_start_date, form.date, Number(selectedSub?.flock_start_week || 0));
  const breeds = selectedSub ? (selectedSub.breeds?.length ? selectedSub.breeds : [selectedSub.breed || "kirmizi"]) : [];
  const multi = breeds.length > 1;
  const [activeBreed, setActiveBreed] = useState("");
  const curBreed = breeds.includes(activeBreed) ? activeBreed : breeds[0];

  useEffect(() => {
    const sub = selectedCoop !== "all" ? subCoopsAll.find((c) => c.id === selectedCoop) : subCoopsAll[0];
    if (sub) { setLocId(sub.parent_id || ""); setForm((f) => ({ ...f, coop_id: sub.id })); }
    // eslint-disable-next-line
  }, [selectedCoop, coops]);

  const onLocChange = (id) => {
    setLocId(id);
    const first = subCoopsAll.find((c) => c.parent_id === id);
    setForm((f) => ({ ...f, coop_id: first?.id || "" }));
  };

  const load = useCallback(async () => {
    const q = selectedCoop !== "all" ? `?coop_id=${selectedCoop}` : "";
    const { data } = await api.get(`/production${q}`);
    setRecords(data);
  }, [selectedCoop]);
  useEffect(() => { load(); }, [load]);

  // auto carry-over for the selected sub-coop/date
  useEffect(() => {
    if (!form.coop_id || !form.date) { setCarryover(0); setCarryoverKnown(true); return; }
    api.get(`/production/carryover?coop_id=${form.coop_id}&date=${form.date}`)
      .then(({ data }) => { setCarryover(data.opening_chickens || 0); setCarryoverKnown(true); })
      .catch(async () => {
        setCarryoverKnown(false);
        const sub = subCoopsAll.find((c) => c.id === form.coop_id);
        setCarryover(await offlineCarryover(form.coop_id, form.date, sub?.chicken_count));
      });
    // eslint-disable-next-line
  }, [form.coop_id, form.date, records]);

  // aynı kümes+tarih için mevcut kayıt varsa forma yükle (aynı-gün düzeltme + kalıcı bakiye)
  useEffect(() => {
    const ex = records.find((r) => r.coop_id === form.coop_id && r.date === form.date);
    const bs = ex?.breed_sizes && Object.keys(ex.breed_sizes).length ? ex.breed_sizes : (ex && breeds[0] ? { [breeds[0]]: ex.sizes } : {});
    setForm((f) => ({ ...f, deaths: ex ? String(ex.deaths ?? "") : "", sizes: ex ? { ...ex.sizes } : {}, breedSizes: JSON.parse(JSON.stringify(bs)), defects: ex ? { ...ex.defects } : {} }));
    // eslint-disable-next-line
  }, [form.coop_id, form.date, records]);

  const num = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v));
  const existing = records.find((r) => r.coop_id === form.coop_id && r.date === form.date);
  const openingBase = existing ? (existing.opening_chickens ?? carryover) : carryover;
  const deaths = num(form.deaths);
  const closing = Math.max(0, openingBase - deaths); // Güncel Tavuk = Devreden − Ölen (anlık)
  const sizeVal = (k) => (multi ? breeds.reduce((s, b) => s + num(form.breedSizes[b]?.[k]), 0) : num(form.sizes[k]));
  const breedTotal = (b) => EGG_ORDER.reduce((s, k) => s + num(form.breedSizes[b]?.[k]), 0);
  const totalCartons =
    EGG_ORDER.reduce((s, k) => s + sizeVal(k), 0) +
    DEFECT_ORDER.reduce((s, k) => s + num(form.defects[k]), 0);
  const totalAdet =
    EGG_ORDER.reduce((s, k) => s + sizeVal(k) * (MULTIPLIERS[k] || 0), 0) +
    DEFECT_ORDER.reduce((s, k) => s + num(form.defects[k]) * (MULTIPLIERS[k] || 0), 0);
  const yieldPct = closing > 0 ? (totalAdet / closing) * 100 : 0;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSize = (k, v) => setForm((f) => (multi
    ? { ...f, breedSizes: { ...f.breedSizes, [curBreed]: { ...(f.breedSizes[curBreed] || {}), [k]: v } } }
    : { ...f, sizes: { ...f.sizes, [k]: v } }));
  const sizeInput = (k) => (multi ? form.breedSizes[curBreed]?.[k] : form.sizes[k]) ?? "";
  const setDefect = (k, v) => setForm((f) => ({ ...f, defects: { ...f.defects, [k]: v } }));

  const save = async () => {
    if (!form.coop_id) return toast.error("Lütfen kümes seçin");
    if (!form.date) return toast.error("Tarih zorunludur");
    setSaving(true);
    try {
      const payload = {
        coop_id: form.coop_id,
        date: form.date,
        flock_age_weeks: age.currentWeek,
        opening_chickens: openingBase,
        deaths,
        closing_chickens: closing,
        sizes: Object.fromEntries(EGG_ORDER.map((k) => [k, sizeVal(k)])),
        defects: Object.fromEntries(DEFECT_ORDER.map((k) => [k, num(form.defects[k])])),
      };
      if (multi) payload.breed_sizes = Object.fromEntries(breeds.map((b) => [b, Object.fromEntries(EGG_ORDER.map((k) => [k, num(form.breedSizes[b]?.[k])]))]));
      // Çevrimdışıyken devreden tavuk bilinmiyorsa sunucudan yeniden hesaplamasını iste (zincir bozulmasın)
      if (!existing && !carryoverKnown) payload.recalc_opening = true;
      if (existing) await api.put(`/production/${existing.id}`, payload);
      else await api.post("/production", payload);
      toast.success(`Kayıt başarılı! Güncel canlı tavuk: ${fmt(closing)} adet`);
      setForm((f) => ({ ...emptyForm(f.coop_id), date: f.date }));
      firstInput.current?.focus();
      load();
    } catch (e) {
      toast.error(errMsg(e, "Kayıt başarısız"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/production/${id}`);
    toast.success("Kayıt silindi");
    load();
  };

  return (
    <div>
      <PageHeader title="Günlük Üretim Girişi" subtitle="Boylama, fire ve randıman takibi — Devreden tavuk önceki günden otomatik gelir, sadece ölüm girin." />

      {selectedSub && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-4 py-1.5 text-sm font-semibold text-primary" data-testid="flock-status">
          🏷️ Sürü Durumu: {age.formattedText}
        </div>
      )}

      {/* Entry card */}
      <div className="rounded-xl border border-border bg-card overflow-hidden mb-6" data-testid="production-form">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 p-5 border-b border-border">
          <Field label="Ana Kümes / Lokasyon">
            <select ref={firstInput} value={locId} onChange={(e) => onLocChange(e.target.value)} data-testid="prod-location"
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">
              <option value="">Seçin</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
          <Field label="Alt Kümes">
            <select value={form.coop_id} onChange={(e) => setField("coop_id", e.target.value)} data-testid="prod-coop"
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">
              <option value="">Seçin</option>
              {subCoops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Tavuk Çeşidi">
            <div data-testid="prod-breed" className="h-9 flex items-center px-3 rounded-md bg-secondary/60 border border-border text-sm font-medium">
              {selectedSub ? breeds.map((b) => BREED_LABELS[b] || b).join(" + ") : "-"}
            </div>
          </Field>
          <Field label="Tarih">
            <input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="prod-date"
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none" />
          </Field>
          <ReadField label="Sürü Yaşı (Hf)" testid="prod-age"><span>{age.currentWeek}</span></ReadField>
          <ReadField label="Hayvan Yaş Günü" testid="prod-ageday"><span>{age.totalDays}</span></ReadField>
          <ReadField label="Devreden Tavuk" testid="prod-opening"><span>{fmt(openingBase)}</span></ReadField>
          <NumField label="Ölüm / Telef" value={form.deaths} onChange={(v) => setField("deaths", v)} testid="prod-deaths" />
          <ReadField label="Güncel Tavuk" testid="prod-closing" accent><span>{fmt(closing)}</span></ReadField>
          {!carryoverKnown && !existing && (
            <div className="col-span-full text-xs text-amber-600" data-testid="prod-offline-carryover-note">
              Çevrimdışı hesaplandı (cihazdaki son kayıtlardan). İnternet gelince sunucu kesin değeri doğrular.
            </div>
          )}
        </div>

        {/* Boylama grid */}
        <div className="p-5 border-b border-border">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Yumurta Boylama (Karton)</div>
            {multi && (
              <div className="inline-flex rounded-full border border-border p-1 bg-secondary/40" data-testid="prod-breed-tabs">
                {breeds.map((b) => (
                  <button key={b} type="button" onClick={() => setActiveBreed(b)} data-testid={`prod-breed-tab-${b}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${curBreed === b ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {BREED_LABELS[b] || b} <span className="opacity-75 tabular-nums">({fmt(breedTotal(b))})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-11 border border-border rounded-lg overflow-hidden">
            {meta.egg_sizes.map((s) => (
              <div key={s.key} className="border-r border-b border-border last:border-r-0">
                <div className="px-2 py-1.5 text-[11px] font-semibold text-center bg-secondary/60 border-b border-border">{s.label}</div>
                <input type="number" min="0" className="grid-input text-center" value={sizeInput(s.key)}
                  onChange={(e) => setSize(s.key, e.target.value)} data-testid={`size-${s.key}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Fire grid */}
        <div className="p-5 flex flex-col lg:flex-row gap-5">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-3">Fire & Kalite</div>
            <div className="grid grid-cols-4 border border-border rounded-lg overflow-hidden max-w-lg">
              {meta.defects.map((d) => (
                <div key={d.key} className="border-r border-border last:border-r-0">
                  <div className="px-2 py-1.5 text-[11px] font-semibold text-center bg-secondary/60 border-b border-border">{d.label}</div>
                  <input type="number" min="0" className="grid-input text-center" value={form.defects[d.key] ?? ""}
                    onChange={(e) => setDefect(d.key, e.target.value)} data-testid={`defect-${d.key}`} />
                </div>
              ))}
            </div>
          </div>

          {/* live calc — rendered via child component to bypass editor static-tree snapshot */}
          <CalcCard cartons={totalCartons} adet={totalAdet} yieldPct={yieldPct} />
        </div>

        <div className="px-5 pb-5 flex justify-end">
          <button onClick={save} disabled={saving} data-testid="prod-save"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50">
            <Save size={16} /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Records table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 text-left">
                <Th>Tarih</Th><Th>Kümes</Th><Th>Yaş</Th><Th>Tavuk</Th><Th>Ölüm</Th>
                {meta.egg_sizes.map((s) => <Th key={s.key} className="text-right">{s.label}</Th>)}
                <Th className="text-right">Fire</Th>
                <Th className="text-right">Toplam</Th><Th className="text-right">Rand.%</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? <EmptyRow colSpan={meta.egg_sizes.length + 9} /> :
                records.map((r) => {
                  const fireTotal = DEFECT_ORDER.reduce((s, k) => s + (r.defects?.[k] || 0), 0);
                  return (
                    <tr key={r.id} className="border-t border-border hover:bg-secondary/30" data-testid={`prod-row-${r.id}`}>
                      <Td>{r.date}</Td>
                      <Td>{coops.find((c) => c.id === r.coop_id)?.name || "-"}</Td>
                      <Td>{r.flock_age_weeks}</Td>
                      <Td>{fmt(r.closing_chickens)}</Td>
                      <Td className="text-destructive">{fmt(r.deaths)}</Td>
                      {EGG_ORDER.map((k) => <Td key={k} className="text-right tabular-nums">{fmt(r.sizes?.[k] || 0)}</Td>)}
                      <Td className="text-right tabular-nums text-amber-600">{fmt(fireTotal)}</Td>
                      <Td className="text-right font-semibold tabular-nums">{fmt(r.total_production)}</Td>
                      <Td className="text-right font-semibold tabular-nums text-primary">%{fmtDec(r.yield_pct)}</Td>
                      <Td>
                        <button onClick={() => remove(r.id)} data-testid={`prod-delete-${r.id}`} className="text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">{label}</label>
    {children}
  </div>
);
const NumField = ({ label, value, onChange, testid }) => (
  <Field label={label}>
    <input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-right tabular-nums focus:ring-2 focus:ring-primary outline-none" />
  </Field>
);
const ReadField = ({ label, testid, accent, children }) => (
  <Field label={label}>
    <div data-testid={testid} className={`h-9 flex items-center justify-end px-3 rounded-md border border-border text-sm font-semibold tabular-nums ${accent ? "bg-primary/10 text-primary" : "bg-secondary/60"}`}>
      {children}
    </div>
  </Field>
);
const Th = ({ children, className = "" }) => <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 whitespace-nowrap ${className}`}>{children}</td>;


function CalcCard({ cartons, adet, yieldPct }) {
  return (
    <div className="flex items-center gap-4 rounded-lg bg-primary/5 border border-primary/20 px-5 py-4">
      <Calculator className="text-primary" size={24} />
      <div className="flex gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Toplam Karton</div>
          <CalcVal testid="calc-cartons">{fmt(cartons)}</CalcVal>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Toplam Adet</div>
          <CalcVal testid="calc-total">{fmt(adet)}</CalcVal>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Randıman</div>
          <CalcVal testid="calc-yield" primary>{`%${fmtDec(yieldPct)}`}</CalcVal>
        </div>
      </div>
    </div>
  );
}
function CalcVal({ testid, primary, children }) {
  return (
    <div data-testid={testid} className={`font-head text-2xl font-bold tabular-nums${primary ? " text-primary" : ""}`}>
      {children}
    </div>
  );
}
